import { useEffect } from 'react';

export const getFileSize = (size) => {
  if (size === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(size) / Math.log(k));
  return `${parseFloat((size / k ** i).toFixed(2))} ${sizes[i]}`;
};

export const getFileType = (fileName) =>
  fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length) || fileName;

// Detect if clicking outside of dropdown. To close it
export const detectClickOutside = (ref, callback) => {
  useEffect(() => {
    function handleClickOutside(event) {
      if (event.target === null) {
        return;
      }
      if (ref.current && !ref.current.contains(event.target)) {
        callback(event);
      }
    }

    // Bind the event listener
    document.addEventListener('click', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('click', handleClickOutside);
    };
  });
};

export const sortObjectsByProp = (objects, prop, ascending = true) => {
  const compare = (a, b) => {
    if (a[prop] < b[prop]) {
      return ascending ? -1 : 1;
    }
    if (a[prop] > b[prop]) {
      return ascending ? 1 : -1;
    }
    return 0;
  };

  return [...objects].sort(compare);
};

export const sortObjectsPropByProp = (objects, objectsProp, prop, ascending = true) => {
  const compare = (a, b) => {
    if (a[prop][objectsProp] < b[prop][objectsProp]) {
      return ascending ? -1 : 1;
    }
    if (a[prop][objectsProp] > b[prop][objectsProp]) {
      return ascending ? 1 : -1;
    }
    return 0;
  };

  return [...objects].sort(compare);
};

export const groupObjects = (objects, groupBy) =>
  objects?.reduce((accum, object) => {
    const updated = { ...accum };
    if (Object.hasOwnProperty.call(updated, object[groupBy])) {
      updated[object[groupBy]].push(object);
    } else {
      updated[object[groupBy]] = [object];
    }

    return updated;
  }, {});

export const formatCurrency = (value, useDecimals = true, invalidValue = '$0') => {
  if (value === null || Number.isNaN(value)) {
    return invalidValue;
  }

  let newValue = `${value}`;
  if (newValue.includes('$')) {
    newValue = newValue.replace('$', '');
  }

  const parsedValue = parseFloat(newValue, 10);
  if (Number.isNaN(parsedValue)) {
    return invalidValue;
  }

  const formatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: useDecimals ? 2 : 0,
    maximumFractionDigits: useDecimals ? 2 : 0,
  });

  return formatter.format(parsedValue).toLocaleString();
};

export const toCapitalizedWords = (name) => {
  const words = name.match(/[A-Za-z][a-z]*/g) || [];

  return words.map(capitalize).join(' ');
};

export const capitalize = (word) => word.charAt(0).toUpperCase() + word.substring(1);

export const findObjectByProperty = (objects, id, property = 'objectId') =>
  objects?.find((obj) => obj?.[property] === id);

export const objectExists = (objects, id, property = 'objectId') =>
  objects?.includes((obj) => obj?.[property] === id);

export const isSet = (value) => value !== null && value !== undefined;
export const findObjectIndexByProperty = (objects, id, property = 'objectId') =>
  objects?.findIndex((obj) => obj?.[property] === id);

export const emptyObject = (value) =>
  value && Object.keys(value).length === 0 && Object.getPrototypeOf(value) === Object.prototype;

/** Check for both null and undefined * */
export const isVoid = (item) => item === null || item === undefined;

/** Useful for checking whether a string has any value or is empty * */
export const isEmpty = (item) => item === null || item === undefined || item === '';

/** Validate an email. Taken from https://bobbyhadz.com/blog/react-check-if-email-is-valid * */
export const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

export const isObject = (obj) => obj && typeof obj === 'object' && !Array.isArray(obj);

/**
 * Immutable deep merge.
 * Adapted from https://stackoverflow.com/questions/27936772/how-to-deep-merge-instead-of-shallow-merge
 */
export const mergeDeep = (target, source, depth = 0, maxDepth = 20) => {
  const output = { ...target };
  if (depth < maxDepth && isObject(target) && isObject(source)) {
    // To prevent infinite recursion
    const newDepth = depth + 1;
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        // If we have a matching key, merge them
        if (!(key in target)) {
          // Key does not exist in target, so add it as new
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = mergeDeep(target[key], source[key], newDepth, maxDepth);
        }
      } else {
        // Not an object, so replace
        // TODO merging two lists should try to add list2 to list1 instead of replacing
        Object.assign(output, { [key]: source[key] });
      }
    });
  }

  return output;
};

/**
 * Empty function
 */
export const noop = () => {
  // Empty function
};

/**
 * Generate a random string of characters of length characters long
 * "borrowed" from https://stackoverflow.com/questions/1349404/generate-random-string-characters-in-javascript
 */
export const generateRandomString = (length) => {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i += 1) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};
